"use client";

import { ArrowUp, Loader2 } from "lucide-react";
import dynamic from "next/dynamic";
import { useRef, useState, useTransition } from "react";
import { EmojiPicker } from "@/components/emoji/emoji-picker";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Text } from "@/components/ui/text";
import UserAvatar from "@/components/user-avatar";
import { createComment } from "@/lib/actions/comments";
import { isHtmlEmpty } from "@/lib/sanitize-html";
import { cn } from "@/lib/utils";
import { useCommentsContextOptional } from "./comments-list-client";

// Lazy-load RichTextEditorMinimal to improve HMR performance
const RichTextEditorMinimal = dynamic(
	() => import("./rich-text-editor-minimal"),
	{
		loading: () => (
			<div className="flex h-9 w-full min-w-0 items-end pb-0" aria-hidden>
				<div className="h-3.5 w-[88%] max-w-md rounded-sm bg-black/10 dark:bg-white/10 motion-safe:animate-pulse" />
			</div>
		),
		ssr: false,
	},
);

interface CommentFormProps {
	/** Exactly one comment target must be set. */
	postId?: number;
	submissionId?: number;
	editorialSlug?: string;
	parentId?: number | null;
	onSuccess?: () => void;
	onCancel?: () => void;
	/** Card layout + primary Reply action (post thread root only). */
	prominent?: boolean;
	/** Inline reply row avatar; falls back to `useCommentsContextOptional` when omitted. */
	composerAvatar?: { src?: string | null; alt: string } | null;
	/** Lock editor geometry on mobile (prevents iOS zoom + layout morph). */
	stableOnMobile?: boolean;
}

export function CommentForm({
	postId,
	submissionId,
	editorialSlug,
	parentId = null,
	onSuccess,
	onCancel,
	prominent = false,
	composerAvatar: composerAvatarProp,
	stableOnMobile = false,
}: CommentFormProps) {
	// Optimistic comment updates + inline reply avatar when inside CommentsListClient
	const commentsContext = useCommentsContextOptional();
	const composerAvatar =
		composerAvatarProp ?? commentsContext?.composerAvatar ?? null;

	const hasPost = postId != null;
	const hasSubmission = submissionId != null;
	const hasEditorial = editorialSlug != null;
	const [isSubmitting, startTransition] = useTransition();
	const [error, setError] = useState<string | null>(null);
	const [body, setBody] = useState("");
	const [insertTextRequest, setInsertTextRequest] = useState<{
		id: number;
		text: string;
	} | null>(null);
	const optimisticAddedRef = useRef(false);
	const isSubmittingRef = useRef(false);

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		if (isSubmittingRef.current) return;
		if (isHtmlEmpty(body)) {
			setError("Comment cannot be empty");
			return;
		}

		setError(null);
		optimisticAddedRef.current = false;
		isSubmittingRef.current = true;

		startTransition(async () => {
			// Optimistic update once per submit (avoids duplicate in Strict Mode / double-invoke)
			if (commentsContext && !optimisticAddedRef.current) {
				if (hasPost) {
					optimisticAddedRef.current = true;
					commentsContext.addOptimisticComment({
						body: body.trim(),
						postId: postId as number,
						parentId: parentId ?? null,
					});
				} else if (hasSubmission) {
					optimisticAddedRef.current = true;
					commentsContext.addOptimisticComment({
						body: body.trim(),
						submissionId: submissionId as number,
						parentId: parentId ?? null,
					});
				} else if (hasEditorial) {
					optimisticAddedRef.current = true;
					commentsContext.addOptimisticComment({
						body: body.trim(),
						editorialSlug,
						parentId: parentId ?? null,
					});
				}
			}

			try {
				const result = await createComment({
					post_id: hasPost ? postId : null,
					submission_id: hasSubmission ? submissionId : null,
					editorial_slug: hasEditorial ? editorialSlug : null,
					parent_id: parentId ?? undefined,
					body: body.trim(),
				});

				if (result.success) {
					setBody("");
					const data = (
						result as {
							success: true;
							data: Parameters<
								NonNullable<typeof commentsContext>["addConfirmComment"]
							>[0];
						}
					).data;
					if (data && commentsContext) {
						commentsContext.addConfirmComment(data);
					}
					onSuccess?.();
				} else {
					setError(result.error || "Failed to post comment");
				}
			} catch (err) {
				setError(
					err instanceof Error ? err.message : "An unexpected error occurred",
				);
			} finally {
				isSubmittingRef.current = false;
			}
		});
	};
	const placeholder =
		submissionId != null && !parentId
			? "Leave a comment..."
			: parentId != null
				? "Write your reply..."
				: prominent
					? "Write a comment..."
					: "Write your reply...";

	const submitLabel = prominent && parentId == null ? "Comment" : "Reply";
	const plainBodyLength = body
		.replace(/<[^>]*>/g, "")
		.replace(/&nbsp;/g, " ")
		.trim().length;
	const canSubmit = !isHtmlEmpty(body);

	return (
		<form
			onSubmit={handleSubmit}
			className={cn("w-full min-w-0 max-w-full", prominent ? "pt-0" : "pt-0")}
		>
			<div
				className={cn(
					"flex w-full min-w-0 items-start",
					prominent ? "gap-4 py-3 md:py-4" : "gap-3 py-2.5 md:px-1 md:py-3",
				)}
			>
				<div className={cn("shrink-0", prominent ? "pt-1" : "pt-1.5")}>
					<UserAvatar
						src={composerAvatar?.src}
						alt={composerAvatar?.alt ?? "You"}
						size={prominent ? 32 : 28}
						className="shrink-0 ring-1 ring-black/5 dark:ring-white/10"
					/>
				</div>
				<div className="min-w-0 flex-1">
					<div
						data-comment-composer-shell={!prominent ? "" : undefined}
						className={cn(
							"transition-[border-color,background-color,box-shadow,border-radius] duration-150",
							prominent
								? "px-4 py-3  md:px-5 md:py-4"
								: [
										"flex min-h-14 items-center gap-2 rounded-full border border-black/10 bg-transparent px-4 py-1.5",
										"shadow-[inset_0_1px_0_rgb(255_255_255_/_0.65)]",
										"focus-within:border-black/25 focus-within:bg-white",
										"dark:border-white/10 dark:bg-white/[0.045] dark:shadow-none dark:focus-within:border-white/25 dark:focus-within:bg-white/[0.06]",
									],
						)}
					>
						<div className="min-w-0 flex-1">
							<RichTextEditorMinimal
								value={body}
								onChange={setBody}
								placeholder={placeholder}
								minHeight={prominent ? "120px" : "44px"}
								surface="underline"
								stableOnMobile={stableOnMobile}
								insertTextRequest={insertTextRequest}
								expandOnFocus={!prominent}
								trailingActions={
									prominent ? undefined : (
										<>
											<EmojiPicker
												label="Insert emoji"
												onSelect={(emoji) =>
													setInsertTextRequest({ id: Date.now(), text: emoji })
												}
												disabled={isSubmitting}
												triggerClassName="h-8 w-8 shrink-0 p-0 text-content-tertiary"
											/>
											<Button
												type="submit"
												size="icon-sm"
												aria-label={
													isSubmitting ? "Posting reply" : "Post reply"
												}
												disabled={isSubmitting || !canSubmit}
												className="size-9 shrink-0 rounded-full p-0"
											>
												{isSubmitting ? (
													<Loader2
														className="size-4 animate-spin"
														strokeWidth={2}
														aria-hidden="true"
													/>
												) : (
													<ArrowUp
														className="size-5"
														strokeWidth={2.4}
														aria-hidden="true"
													/>
												)}
											</Button>
										</>
									)
								}
							/>
						</div>
					</div>
					{prominent ? (
						<div className="mt-3 flex flex-wrap items-center justify-between gap-3">
							<Text as="p" size="xs" color="tertiary">
								{plainBodyLength}/1500
							</Text>
							<div className="flex items-center gap-2">
								<EmojiPicker
									label="Insert emoji"
									onSelect={(emoji) =>
										setInsertTextRequest({ id: Date.now(), text: emoji })
									}
									disabled={isSubmitting}
									triggerClassName="h-8 w-8 p-0 text-content-tertiary"
								/>
								{parentId != null ? (
									<Button
										type="button"
										variant="ghost"
										size="sm"
										onClick={onCancel}
										disabled={isSubmitting}
									>
										Cancel
									</Button>
								) : null}
								<Button
									type="submit"
									variant="default"
									size="sm"
									className="rounded-full px-5 uppercase"
									disabled={isSubmitting}
								>
									{isSubmitting ? "Posting..." : submitLabel}
								</Button>
							</div>
						</div>
					) : null}
				</div>
			</div>
			{error ? (
				<Alert variant="destructive" className="mt-3">
					<AlertDescription>{error}</AlertDescription>
				</Alert>
			) : null}
		</form>
	);
}
